import { effect, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SessionQuery } from '@core/session/store/session.query';
import { isKioskWorkstation } from '@features/kiosk/kiosk-mode';
import { environment } from '../../../environments/environment';

/** События, по которым АРМ считается занятым человеком. */
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

/** Как часто проверяется простой. Он же шаг часов на экране ожидания. */
const TICK_MS = 1000;

const DEFAULT_IDLE_DELAY_MS = 2 * 60 * 1000;

/**
 * Экран ожидания АРМ в режиме ПАК.
 *
 * Показывается, когда за рабочим местом никого нет, и снимается первым же касанием или
 * нажатием клавиши.
 *
 * **Только до входа в систему.** Это не декоративное ограничение: РАИС показывает живые
 * параметры пациента, и заставка, накрывающая карту наблюдения через две минуты без
 * движения мыши, скрыла бы данные, за которыми человек и смотрит на экран. Поэтому простой
 * считается лишь пока сессии нет — а выход по неактивности остаётся зоной
 * `AppComponent.setupIdleHooks` и модального окна входа.
 */
@Injectable({ providedIn: 'root' })
export class KioskIdleService {
  private sessionQuery = inject(SessionQuery);

  private readonly enabled = isKioskWorkstation();
  private readonly idleDelayMs = environment.settings.kioskIdleDelayMs ?? DEFAULT_IDLE_DELAY_MS;

  private readonly visible = signal(false);
  private lastActivityAt = Date.now();
  private ticker: ReturnType<typeof setInterval> | null = null;

  /** Показан ли сейчас экран ожидания. */
  readonly isVisible$ = this.visible.asReadonly();

  constructor() {
    if (!this.enabled) return;

    const user = toSignal(this.sessionQuery.user$, { initialValue: null });

    effect(() => {
      if (user()) {
        this.stopWatching();
      } else {
        this.startWatching();
      }
    });

    for (const type of ACTIVITY_EVENTS) {
      window.addEventListener(type, this.onActivity, { passive: true, capture: true });
    }
  }

  /** Снять экран ожидания — например, по клику на него самом. */
  dismiss(): void {
    this.onActivity();
  }

  private onActivity = (): void => {
    this.lastActivityAt = Date.now();

    if (this.visible()) {
      this.visible.set(false);
    }
  };

  private startWatching(): void {
    this.lastActivityAt = Date.now();

    if (this.ticker !== null) return;

    this.ticker = setInterval(() => {
      if (Date.now() - this.lastActivityAt >= this.idleDelayMs) {
        this.visible.set(true);
      }
    }, TICK_MS);
  }

  private stopWatching(): void {
    if (this.ticker !== null) {
      clearInterval(this.ticker);
      this.ticker = null;
    }

    this.visible.set(false);
  }
}
